import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useState } from 'react';

import {
  selectIsEditable,
  selectLatestStatus,
  selectStudyPeriodEndDate,
  selectStudyPeriodStartDate,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { useDatasetDateBounds } from '../../../../../hooks/common/useDatasetDateBounds';
import { useEmitResearchAssistantUpdate } from '../../../../../hooks/common/useEmitResearchAssistantUpdate';

export const useStudyPeriodInlineEdit = () => {
  const latestStatus = useCohortDefinitionStore(selectLatestStatus);
  const isProcessing =
    latestStatus === CohortDefinitionStatus.PROCESSING ||
    latestStatus === CohortDefinitionStatus.FINAL;
  const isEditable = useCohortDefinitionStore(selectIsEditable);
  const viewMode = !isEditable || isProcessing;

  const emitResearchAssistantUpdate = useEmitResearchAssistantUpdate(viewMode);

  const { minDate: datasetMinDate, maxDate: datasetMaxDate } = useDatasetDateBounds();

  const storedStartDate = useCohortDefinitionStore(selectStudyPeriodStartDate);
  const storedEndDate = useCohortDefinitionStore(selectStudyPeriodEndDate);

  const [isEditing, setIsEditing] = useState(false);
  const [hasDateBoundsError, setHasDateBoundsError] = useState(false);
  const [studyPeriodStartDate, setStudyPeriodStartDate] = useState<Dayjs | null>(null);
  const [studyPeriodEndDate, setStudyPeriodEndDate] = useState<Dayjs | null>(null);

  const startEdit = useCallback(() => {
    setStudyPeriodStartDate(
      storedStartDate ? dayjs.utc(new Date(storedStartDate)) : datasetMinDate
    );
    setStudyPeriodEndDate(storedEndDate ? dayjs.utc(new Date(storedEndDate)) : datasetMaxDate);
    setHasDateBoundsError(false);
    setIsEditing(true);
  }, [storedStartDate, storedEndDate, datasetMinDate, datasetMaxDate]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const hasStudyPeriodError = !studyPeriodStartDate || !studyPeriodEndDate;
  const hasError = hasStudyPeriodError || hasDateBoundsError;

  const toISO = (d: Dayjs | null | undefined): string | undefined =>
    d?.isValid() ? d.toISOString() : undefined;

  const initialStartISO = toISO(
    storedStartDate ? dayjs.utc(new Date(storedStartDate)) : datasetMinDate
  );
  const initialEndISO = toISO(storedEndDate ? dayjs.utc(new Date(storedEndDate)) : datasetMaxDate);

  const hasChanges =
    toISO(studyPeriodStartDate) !== initialStartISO || toISO(studyPeriodEndDate) !== initialEndISO;

  const saveEdit = useCallback(() => {
    const nextStudyPeriodRestrictions = {
      overallStudyPeriodRestriction: {
        isEnabled: true,
        startDate: studyPeriodStartDate?.toISOString() || '',
        endDate: studyPeriodEndDate?.toISOString() || '',
      },
      baselinePeriodRestriction: undefined,
      followUpPeriodRestriction: undefined,
    };

    if (!viewMode) {
      useCohortDefinitionStore.getState().setStudyPeriod({
        overallStudyPeriodRestriction: nextStudyPeriodRestrictions.overallStudyPeriodRestriction,
        changeStatus: undefined,
        changedBy: undefined,
      });
    }

    const {
      subjectVariables,
      indexDate,
      simpleEvents,
      derivedEvents,
      analyticalVariables,
      criteria,
    } = useCohortDefinitionStore.getState();
    const nextCohortDefinition = {
      studyPeriodRestrictions: nextStudyPeriodRestrictions,
      subjectVariables,
      indexDate,
      simpleEvents,
      derivedEvents,
      analyticalVariables,
      criteria,
    };
    emitResearchAssistantUpdate(nextCohortDefinition);
    setIsEditing(false);
  }, [studyPeriodStartDate, studyPeriodEndDate, viewMode, emitResearchAssistantUpdate]);

  return {
    isEditing,
    viewMode,
    hasError,
    hasChanges,
    isEditable,
    startEdit,
    cancelEdit,
    saveEdit,
    studyPeriodStartDate,
    setStudyPeriodStartDate,
    studyPeriodEndDate,
    setStudyPeriodEndDate,
    setHasDateBoundsError,
    datasetMinDate: datasetMinDate ?? undefined,
    datasetMaxDate: datasetMaxDate ?? undefined,
  };
};
